define('ch_search',['ch_utils'], function ({ debounce }) {
    // initialize the view
    if (!window.Filters) {
        window.Filters = {};
    }

    // @todo make them ids
    const searchForm = document.querySelector('.js-search-form');
    const searchField = document.querySelector('.js-search-by-keyword input');
    const resultsElement = document.getElementById('header-search-results');
    const maxResult = 10;

    const mobileSearchButton = document.getElementById('js-mobile-search');
    const mobileSearchForm = document.getElementById('mobile-search-section');

    const removedSearchTermsExpr = new RegExp([
        'class',
        'classes',
        'course',
        'courses',
        'training',
        'workshop',
    ].map((word) => `\\b${word}\\b`).join('|'), 'ig');

    const debouncedRenderSuggestions = debounce(renderSuggestions, 200);

    // the "exact match" is a shortcut to link directly to a category
    // instead of submitting a keyword search upon submission
    let exactCategoryMatch;

    // Sequence number for suggestion renders. The endpoint can take most of a second while
    // input is debounced at 200ms, so a response routinely arrives after the user has typed
    // again. Without this, a slow reply for "sql" repaints the menu after a newer keystroke
    // already cleared it, which is the same stale-results symptom by a different route.
    let latestRenderToken = 0;

    // deal with displaying the mobile search field as well...
    if (mobileSearchButton && mobileSearchForm) {
        // Collapse the bar here rather than in the template: some responsive
        // pages include the menu bar but never execute this module, and there
        // the bar must stay visible as a plain working form.
        mobileSearchForm.setAttribute('hidden', '');
        mobileSearchButton.setAttribute('aria-expanded', 'false');
        mobileSearchButton.addEventListener('click', () => {
            const isHidden = mobileSearchForm.toggleAttribute('hidden');
            mobileSearchButton.setAttribute('aria-expanded', String(!isHidden));
            if (!isHidden) {
                const mobileSearchInput = mobileSearchForm.querySelector('input[name="kw"]');
                if (mobileSearchInput) {
                    mobileSearchInput.focus();
                }
            }
        });
    }

    // ---- keyboard navigation over the rendered menu ----
    //
    // A native select walks its options with the arrow keys for free; this menu is plain
    // markup, so until now the keyboard could only ever submit the raw keyword. Focus stays
    // in the input (aria-activedescendant carries the active option), matching how the
    // hub picker in ch_semantic_search.js behaves.
    let activeSuggestionIndex = -1;

    function suggestionItems() {
        return resultsElement ?
            Array.from(resultsElement.querySelectorAll('.ui-menu-item-wrapper')) :
            [];
    }

    function setActiveSuggestion(index) {
        const items = suggestionItems();
        if (!items.length) {
            return;
        }

        activeSuggestionIndex = ((index % items.length) + items.length) % items.length;

        items.forEach(function (item, i) {
            item.classList.toggle('ui-state-active', i === activeSuggestionIndex);
            item.setAttribute('aria-selected', String(i === activeSuggestionIndex));
        });

        const active = items[activeSuggestionIndex];
        if (searchField) {
            searchField.setAttribute('aria-activedescendant', active.id);
        }
        if (active.scrollIntoView) {
            active.scrollIntoView({ block: 'nearest' });
        }
    }

    function clearActiveSuggestion() {
        activeSuggestionIndex = -1;
        if (searchField) {
            searchField.removeAttribute('aria-activedescendant');
        }
    }

    // ---- click telemetry ----
    //
    // One beacon per suggestion actually chosen -- click or Enter -- so search_log's
    // "what was searched" gains "which suggestion answered it". sendBeacon survives the
    // navigation the click starts; a failure must never delay or break it, hence the
    // guards and the absence of any response handling.
    function reportSuggestionPick(anchor) {
        try {
            if (!navigator.sendBeacon || !searchField || !anchor) {
                return;
            }
            const items = suggestionItems();
            const payload = JSON.stringify({
                term: searchField.value.trim().toLowerCase(),
                section: anchor.getAttribute('data-type') || '',
                label: (anchor.textContent || '').trim(),
                url: anchor.getAttribute('href') || '',
                position: Math.max(0, items.indexOf(anchor)),
            });
            navigator.sendBeacon(
                '/course/filter/suggest-click',
                new Blob([payload], { type: 'application/json' }),
            );
        } catch (e) {
            // Telemetry only. Never let it interfere with the navigation.
        }
    }


    // set defaults
    if (searchField) {
        searchField.value = Filters.kw || '';

        searchField.setAttribute('role', 'combobox');
        searchField.setAttribute('aria-autocomplete', 'list');
        searchField.setAttribute('aria-expanded', 'false');

        // @todo fetch polyfill
        searchField.addEventListener('input', function () {
            const value = searchField.value.replace(removedSearchTermsExpr, '');

            exactCategoryMatch = null;

            debouncedRenderSuggestions(value);
        });

        // Arrow keys walk the rendered menu; Enter follows the active item; Escape puts
        // the input back the way it was. Runs before the form's submit handling because
        // preventDefault on Enter here stops the submit listener's path entirely.
        searchField.addEventListener('keydown', function (e) {
            const menuVisible = resultsElement && resultsElement.style.display === 'block';
            const items = suggestionItems();

            if (!menuVisible || !items.length) {
                if (e.key === 'Escape' && menuVisible) {
                    resultsElement.style.display = 'none';
                    clearActiveSuggestion();
                }
                return;
            }

            switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveSuggestion(activeSuggestionIndex + 1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveSuggestion(activeSuggestionIndex - 1);
                break;
            case 'Enter':
                if (activeSuggestionIndex >= 0 && items[activeSuggestionIndex]) {
                    e.preventDefault();
                    const anchor = items[activeSuggestionIndex];
                    reportSuggestionPick(anchor);
                    window.location = anchor.getAttribute('href');
                }
                break;
            case 'Escape':
                e.preventDefault();
                resultsElement.style.display = 'none';
                clearActiveSuggestion();
                break;
            }
        });
    }

    // The menu's anchors navigate natively; the beacon rides along. Mousedown-based, so it
    // fires before the mouseup completes the click and the page starts unloading.
    if (resultsElement) {
        resultsElement.addEventListener('click', function (e) {
            const anchor = e.target.closest ? e.target.closest('.ui-menu-item-wrapper') : null;
            if (anchor) {
                reportSuggestionPick(anchor);
            }
        });

        resultsElement.addEventListener('mouseover', function (e) {
            const anchor = e.target.closest ? e.target.closest('.ui-menu-item-wrapper') : null;
            if (anchor) {
                setActiveSuggestion(suggestionItems().indexOf(anchor));
            }
        });
    }

    if (searchForm) {
        // when a user submits their search term in full
        searchForm.addEventListener('submit', function (e) {
            if (exactCategoryMatch && exactCategoryMatch.url) {
                e.preventDefault();
                window.location = exactCategoryMatch.url;
                return;
            }
            else {
                e.preventDefault();
                const value = searchField.value.replace(removedSearchTermsExpr, '');

                // Try one more last before submitting:
                // Two-argument .then rather than a trailing .catch, so a throw inside the
                // success handler cannot also hit the failure handler and submit twice.
                renderSuggestions(value).then(
                    () => {
                        if (exactCategoryMatch && exactCategoryMatch.url) {
                            window.location = exactCategoryMatch.url;
                            return;
                        }
                        searchForm.submit();
                    },
                    () => {
                        // Never strand the user. The native submit was already cancelled
                        // above, so if the suggestions request fails there is nothing left
                        // to carry out the search unless we do it here: the Search button
                        // and the Enter key would otherwise do nothing at all.
                        searchForm.submit();
                    },
                )
            }
        });

        // The old empty 'click' listener here is superseded by the delegated beacon
        // handler on the results element below; the old 'focus' listener referenced an
        // undefined `target` and could never have run without throwing.
    }

    // Two characters, not three. "AI" is a real category and a real search, and at the old
    // threshold typing it did nothing at all: renderSuggestions returned before touching the
    // results element, so the dropdown kept showing whatever the PREVIOUS query had rendered.
    // Typing "sql" then replacing it with "ai" left the SQL results sitting under the box.
    const minSuggestionLength = 2;

    // Endpoint responses, memoised per exact term for the life of the page.
    //
    // Typing is not a stream of distinct queries: people backspace, retype the same word,
    // and hit enter on a term the debounce already fetched a moment ago. Every one of those
    // was a fresh round trip -- measured at 1,257-1,467 ms against a warm-cache floor of
    // 80 ms -- and the submit handler below is the worst of them, because it re-fetches
    // before it will navigate, so pressing enter paid the full latency a second time.
    //
    // Keyed by term alone, which is safe only because the hub cannot change without a
    // navigation (the hub <select> sets window.location), and a navigation empties this.
    const suggestionCache = new Map();
    const suggestionCacheMax = 100;

    // The request that currently owns the menu. A newer one supersedes it: the abandoned
    // response is of no use to anybody, and letting it run keeps a connection busy and an
    // expensive query running for a term the visitor has already typed past.
    let inFlightController = null;

    async function renderSuggestions(value, inheritedToken) {
        // A retry INHERITS its parent's token rather than taking a new one. Allocating a
        // fresh number per retry broke the invariant the paint guard depends on, that the
        // highest token is the newest thing the visitor asked for: an abandoned query's
        // retry is issued later in wall-clock time than a newer keystroke, so it claimed the
        // higher number and the newer, correct results were then discarded unpainted.
        // Shortening the box while a zero-result query was still in flight showed this.
        const token = inheritedToken === undefined ? ++latestRenderToken : inheritedToken;
        const trimmed = value ? value.trim() : '';

        // Below the threshold, clear the box instead of leaving stale results visible.
        // This is the half of the bug that made short queries look wrong rather than empty.
        if (trimmed.length < minSuggestionLength) {
            exactCategoryMatch = null;
            if (resultsElement) {
                resultsElement.innerHTML = '';
                resultsElement.style.display = 'none';
            }
            return;
        }

        if (value && trimmed.length >= minSuggestionLength) {
            const data = await fetchSearchSuggestions(value)

            // Superseded while in flight. Not an error and not an empty result: bail out
            // without touching the menu and without firing the retries below, which would
            // otherwise chase a term nobody is looking at any more.
            if (data === null) {
                return;
            }

            if (token === latestRenderToken) {
                resultsElement.innerHTML = '';
            }
            let result = '';

            const categories = data.data.filter((el) => el.section === 'Category');
            const classes = data.data.filter((el) => el.section === 'Course');
            const schools = data.data.filter((el) => el.section === 'School');

            // No client-side retries any more. The no-space, "&", and singular rewrites --
            // plus shape-word stripping the client never did -- run inside the endpoint now
            // (FilterController::_suggestionsWithRescue), so a zero-result term costs one
            // round trip instead of up to four.

            if (data.data.length === 0) {
                result += 'Press "enter" to search by keyword.';
            } else {
                result += '<ul class="ui-menu ui-widget ui-widget-content ui-autocomplete ui-front" role="listbox">';

                data.data.splice(maxResult);

                // Sequential ids across the three sections, for aria-activedescendant.
                let optionId = 0;

                if (categories.length) {
                    result += '<li class="ui-autocomplete-section">Categories</li>';
                    result += categories.reduce((prev, el) => `${prev}<li class="ui-menu-item" role="presentation">
                        <a class="ui-menu-item-wrapper result" role="option"
                            id="header-suggestion-${optionId++}" data-type="Category" href="${el.url}">
                            ${el.label}
                        </a>
                    </li>`, '');

                    exactCategoryMatch = categories.find((category) => category.label.toLowerCase() === value.trim().toLowerCase());


                    // Try again with "plural" version of search term (yeah, yeah, I know many plurals just add an 's')
                    if (!exactCategoryMatch) {
                        exactCategoryMatch = categories
                            .find((category) => category.label.toLowerCase() === value.trim().toLowerCase() + 's');
                    }
                } else {
                    exactCategoryMatch = null;
                }

                if (classes.length) {
                    result += '<li class="ui-autocomplete-section">Courses</li>';
                    result += classes.reduce((prev, el) => `${prev}<li class="ui-menu-item" role="presentation">
                        <a class="ui-menu-item-wrapper result" role="option"
                            id="header-suggestion-${optionId++}" data-type="Course" href="${el.url}">
                            ${el.label}
                        </a>
                    </li>`, '')
                }

                if (schools.length) {
                    result += '<li class="ui-autocomplete-section">Schools</li>';
                    result += schools.reduce((prev, el) => `${prev}<li class="ui-menu-item" role="presentation">
                        <a class="ui-menu-item-wrapper result" role="option"
                            id="header-suggestion-${optionId++}" data-type="School" href="${el.url}">
                            ${el.label}
                        </a>
                    </li>`, '');
                }

                result += '</ul>';
            }

            // Only the newest render may paint. A stale response must not resurrect a
            // menu that a newer keystroke already cleared or replaced.
            if (token === latestRenderToken) {
                resultsElement.innerHTML = result;
                resultsElement.style.display = 'block';
                clearActiveSuggestion();
            }
        }
    }


    // Resolves to a well-formed payload, or to null when this request was superseded.
    // Never rejects: a non-ok response, a parse failure and a network error all come back as
    // an empty payload, because the submit handler awaits this before it navigates and a
    // throw there used to strand the visitor with a dead Search button.
    async function fetchSearchSuggestions(value) {
        const key = value.trim();

        if (suggestionCache.has(key)) {
            return suggestionCache.get(key);
        }

        if (inFlightController) {
            inFlightController.abort();
        }
        const controller = new AbortController();
        inFlightController = controller;

        let raw = null;
        try {
            const response = await fetch(
                `/course/filter/search-suggestions?search=${encodeURIComponent(value)}`,
                { signal: controller.signal },
            );
            if (response.ok) {
                raw = await response.json();
            }
        } catch (e) {
            // Distinguish "we cancelled this" from "it failed". Only the latter should
            // reach the render path, which treats an empty payload as a real zero-result
            // answer and starts rewriting the term to retry.
            if (controller.signal.aborted) {
                return null;
            }
        } finally {
            if (inFlightController === controller) {
                inFlightController = null;
            }
        }

        const payload = (raw && raw.success && Array.isArray(raw.data)) ?
            raw :
            { success: false, data: [] };

        // Only a real answer is memoised -- including a legitimately empty one, which is
        // worth caching precisely because it is what triggers the retry chain. A failure is
        // not cached, so the next keystroke gets another chance at the endpoint.
        if (payload.success) {
            if (suggestionCache.size >= suggestionCacheMax) {
                suggestionCache.delete(suggestionCache.keys().next().value);
            }
            suggestionCache.set(key, payload);
        }

        return payload;
    }
});

